import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Flame, Snowflake, ThermometerSun, ArrowLeft, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Btn, Badge } from "../components/common";
import AccountDrawer from "../components/AccountDrawer";
import { useAutoRefresh } from "../lib/autoRefresh";

const STAGES = [
  { key: "lead", label: "Lead", cls: "bg-slate-50 border-slate-200" },
  { key: "mql", label: "MQL", cls: "bg-sky-50 border-sky-200" },
  { key: "sql", label: "SQL", cls: "bg-amber-50 border-amber-200" },
  { key: "opportunity", label: "Oportunidade", cls: "bg-emerald-50 border-emerald-200" },
];
const R_META = {
  hot: { cls: "bg-red-100 text-red-700 border-red-200", Icon: Flame },
  warm: { cls: "bg-amber-100 text-amber-700 border-amber-200", Icon: ThermometerSun },
  cold: { cls: "bg-sky-100 text-sky-700 border-sky-200", Icon: Snowflake },
};

export default function LeadKanban() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drag, setDrag] = useState(null);
  const [drawerId, setDrawerId] = useState(null);
  const [overStage, setOverStage] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/crm/qualify", { params: { limit: 500 } });
      setRows(data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(load);

  const cols = STAGES.map((s) => ({ ...s, items: rows.filter((r) => (r.ciclo_vida || "lead") === s.key) }));

  const onDragStart = (r) => setDrag(r);
  const onDrop = async (stage) => {
    if (!drag) return;
    if ((drag.ciclo_vida || "lead") === stage) { setDrag(null); setOverStage(null); return; }
    // Optimista
    setRows((prev) => prev.map((x) => x.id === drag.id ? { ...x, ciclo_vida: stage } : x));
    try {
      await api.put(`/crm/qualify/${drag.id}`, { ciclo_vida: stage });
      toast.success(`${drag.nome} -> ${stage.toUpperCase()}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      load();
    } finally { setDrag(null); setOverStage(null); }
  };

  return (
    <>
      <PageHeader
        overline="CRM"
        title="Kanban de Qualificacao"
        action={
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={() => nav("/app/crm/qualificacao")}><ArrowLeft size={14} /> Lista priorizada</Btn>
            <Btn variant="ghost" onClick={load}><RefreshCw size={14} /> Atualizar</Btn>
          </div>
        }
      />

      <div className="text-xs text-muted2 mb-3">Arraste os cartoes entre colunas para atualizar o ciclo de vida da conta.</div>

      {loading && <div className="text-center text-muted2 py-6">A calcular...</div>}

      <div className="grid grid-cols-4 gap-3 min-h-[60vh]" data-testid="kanban-board">
        {cols.map((c) => (
          <div key={c.key}
            data-testid={`kanban-col-${c.key}`}
            onDragOver={(e) => { e.preventDefault(); setOverStage(c.key); }}
            onDragLeave={() => setOverStage((s) => s === c.key ? null : s)}
            onDrop={() => onDrop(c.key)}
            className={`rounded-lg border-2 ${overStage === c.key ? "border-blue border-dashed bg-blue/5" : c.cls} p-2 flex flex-col`}
          >
            <div className="flex items-center justify-between mb-2 px-1">
              <div className="text-[11px] font-bold uppercase tracking-wider text-navy">{c.label}</div>
              <span className="text-[10px] font-mono bg-white border border-tint rounded px-1.5">{c.items.length}</span>
            </div>
            <div className="flex-1 space-y-1.5 overflow-y-auto max-h-[65vh]">
              {c.items.length === 0 && <div className="text-[11px] text-slate-400 italic text-center py-4">Vazio</div>}
              {c.items.map((r) => {
                const meta = R_META[r.rating || "cold"];
                return (
                  <div key={r.id}
                    draggable
                    onDragStart={() => onDragStart(r)}
                    onDragEnd={() => setDrag(null)}
                    onClick={() => setDrawerId(r.id)}
                    data-testid={`kanban-card-${r.id}`}
                    className="bg-white border border-tint rounded-md p-2 cursor-move hover:border-blue hover:shadow-sm transition"
                  >
                    <div className="flex items-start justify-between gap-1">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-navy truncate">{r.nome}</div>
                        {r.codigo && <div className="text-[9px] font-mono text-blue">{r.codigo}</div>}
                      </div>
                      <div className={`inline-flex items-center gap-0.5 text-[9px] font-bold px-1 py-0.5 rounded border ${meta.cls}`}>
                        <meta.Icon size={9} /> {r.lead_score ?? 0}
                      </div>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1 truncate">{r.setor || "Setor n/d"} - {r.origem || "-"}</div>
                    {r.bant_authority && <div className="text-[10px] text-slate-400 mt-0.5">Auth: {r.bant_authority}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <AccountDrawer accountId={drawerId} open={!!drawerId} onClose={() => { setDrawerId(null); load(); }} />
    </>
  );
}
